import { blinkConstants } from '@thoughtspot/blink-constants';
import { AppModule, useEmbedContext } from '@thoughtspot/blink-context';
import { resolveThemeMode } from '@thoughtspot/custom-style-service';
import {
    Content,
    ContentWidth,
    Layout,
    LeftSideBar,
} from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { isRadiantThemeEnabled } from '@thoughtspot/session-service';
import cx from 'classnames';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import { NavHubContextProvider } from '/@contexts/nav-hub-context';
import { addCustomCssElements, isFullAppEmbed } from '/@utils/embed.util';
import { PERF_EVENT, PERF_KEYS, setPerfLog } from '/@utils/perf-log';
import { LeftNavigationHub } from '../components/nav/navigation/left-navigation-hub.container';
import { InitSessionCheckContainer } from '../components/session-status-check/init-session-check.container';
import { ClientStateContextProvider } from '../contexts/global-client-state';
import {
    applyTheme,
    getCurrentSourceColor,
    reapplyThemeForMode,
} from '../theme/dynamic-theme/apply-theme';
import { registerDynamicThemeListener } from '../theme/dynamic-theme/global-api';
import {
    activateNavThemeBridge,
    deactivateNavThemeBridge,
} from '../theme/dynamic-theme/nav-theme-bridge';
import { setTopNavInternalVariables } from '../theme/top-nav-v3/top-nav-v3-theme-service';
import {
    getUserThemeModePref,
    resolveEffectiveDark,
} from '../theme/user-theme-preference';
import { isLeftPanelVisible } from './authenticated-app.util';
import styles from './authenticated-app-layout.module.scss';
import {
    getAppModule,
    getIsOverlayLeftPanel,
    useIsInsightsPage,
} from './routes-util';

export type AuthenticatedAppLayoutProps = {
    isGlobalNavHidden: boolean;
    isPageContentFullHeight: boolean;
    isLoadingIndicatorVisible: boolean;
};

/**
 * Navigation v3 is the only navigation now, with two left-navigation modes:
 * 1. Hub mode, where the left navigation is loaded separately from the
 *    navigation mfe
 * 2. Overlay mode, where the left navigation is loaded separately as an
 *    overlay window
 */
export const AuthenticatedAppLayout: React.FC<AuthenticatedAppLayoutProps> = ({
    isGlobalNavHidden,
    isPageContentFullHeight,
    isLoadingIndicatorVisible,
}) => {
    setPerfLog(PERF_KEYS.AUTHENTICATED_APP_LAYOUT, PERF_EVENT.START);
    const { sessionService, customStylingService } = useAppContext();
    const embedConfig = useEmbedContext();
    const { isEmbedded } = embedConfig;

    const isInsightsPage = useIsInsightsPage();
    const appModule = getAppModule();
    const isOverlayPanel = getIsOverlayLeftPanel();

    const isEverywhereRoute = !!useMatch('/everywhere/*');
    const isSparkflowPinboardRoute = !!useMatch('/sparkflow/pinboard/*');

    // hideNavigationHub incorporates isEverywhereRoute check to show nav hub on everywhere routes
    const hideNavigationHub = useMemo(() => {
        // Always show nav hub on everywhere routes
        if (isEverywhereRoute) {
            return false;
        }

        // v3 only hides OnBoarding (Admin shows the nav hub).
        return appModule === AppModule.OnBoarding;
    }, [isEverywhereRoute, appModule]);

    const radiantEnabled = isRadiantThemeEnabled();
    const themeType = customStylingService.getConfig()?.getThemeType();
    const config = customStylingService.getConfig();
    const leftNavThemeType = useMemo(() => {
        if (themeType === blinkConstants.themeType.CUSTOM && config) {
            const appPanelColor = config.getAppPanelColor(true)?.color;
            if (appPanelColor) {
                const normalizedAppPanelColor = appPanelColor
                    .toUpperCase()
                    .substring(0, 7);
                const normalizedDarkColor = blinkConstants.themeColor.DARK.toUpperCase().substring(
                    0,
                    7,
                );
                if (normalizedAppPanelColor === normalizedDarkColor) {
                    return blinkConstants.themeType.DARK;
                }
            }
        }
        return themeType;
    }, [themeType, config]);

    // Tracks the active radiant nav mode ('light' | 'dark' | 'brand'),
    // or null when the radiant flow toggle is off / flag is disabled.
    const [radiantNavMode, setRadiantNavMode] = useState<string | null>(null);

    // Maps radiant nav mode to the legacy themeType constant
    // so existing consumers (getIconColor, data-theme-type) work correctly.
    const getEffectiveThemeType = () => {
        if (!radiantNavMode) return leftNavThemeType;
        if (radiantNavMode === 'dark') return blinkConstants.themeType.DARK;
        return blinkConstants.themeType.TWO_TONE;
    };
    const effectiveThemeType = getEffectiveThemeType();

    // Register dynamic theme event bridge and apply the persisted theme
    // on mount. Uses the pure resolveThemeMode() function to deterministically
    // map (appPanelColor, flag) to the correct theme state.
    useEffect(() => {
        if (!radiantEnabled) {
            setRadiantNavMode(null);
            deactivateNavThemeBridge();
            return undefined;
        }

        const cleanup = registerDynamicThemeListener({
            onNavModeChange: ({ active, mode }) => {
                setRadiantNavMode(active ? mode || 'light' : null);
            },
        });

        const rawColor = config?.getRawAppPanelColor?.() || null;
        const resolved = resolveThemeMode(rawColor, true);

        if (resolved.radiant) {
            // Resolve the user's dark/light preference first so applyTheme
            // receives the correct isDark flag and generates the right token set.
            const userId = sessionService?.getUserGuid?.() ?? '';
            const userMode = getUserThemeModePref(userId);
            const userWantsDark = resolveEffectiveDark(userMode);

            // Apply the user's per-user dark/light preference from localStorage.
            // Set data-theme before applyTheme so any code that reads the attribute
            // during token generation sees the correct mode.
            if (userWantsDark) {
                document.documentElement.setAttribute('data-theme', 'dark');
            } else {
                document.documentElement.removeAttribute('data-theme');
            }

            // Apply brand color CSS vars if a hex was set by the admin.
            if (resolved.mode === 'brand' && resolved.sourceHex) {
                const themeVars = applyTheme(resolved.sourceHex, {
                    isDark: userWantsDark,
                });
                addCustomCssElements({
                    customizations: {
                        style: {
                            customCSS: { variables: themeVars },
                        },
                    },
                });
            }

            const hasBrand = resolved.mode === 'brand';
            let navMode: 'brand' | 'dark' | 'light';
            if (hasBrand) {
                navMode = 'brand';
            } else if (userWantsDark) {
                navMode = 'dark';
            } else {
                navMode = 'light';
            }
            setRadiantNavMode(navMode);
        }

        return cleanup;
    }, [radiantEnabled]);

    // Activate / deactivate the nav CSS variable bridge.
    // When active, --left-nav-* vars on #root reference system tokens.
    useEffect(() => {
        if (radiantNavMode) {
            activateNavThemeBridge();
        } else {
            deactivateNavThemeBridge();
        }
    }, [radiantNavMode]);

    // Listen for user theme-mode changes from the preference page.
    // 'system' mode — ThemePreference only writes localStorage and dispatches
    // this event; authenticated-app-layout owns the matchMedia lifecycle.
    useEffect(() => {
        if (!radiantEnabled) return undefined;

        // Tracks the active matchMedia listener so it can be cleaned up when
        // the user switches away from 'system' mode or the component unmounts.
        let activeMediaQuery: MediaQueryList | null = null;
        let activeMediaListener:
            | ((e: MediaQueryListEvent) => void)
            | null = null;

        const applyMode = (isDark: boolean) => {
            if (isDark) {
                document.documentElement.setAttribute('data-theme', 'dark');
            } else {
                document.documentElement.removeAttribute('data-theme');
            }

            // Re-inject brand tokens for the new mode. Without this, the
            // light-mode inline styles set by applyTheme() remain on :root
            // and override [data-theme='dark'] stylesheet rules (inline styles
            // have higher specificity than stylesheet rules).
            reapplyThemeForMode(isDark);

            // getCurrentSourceColor() reflects the latest brand color applied via
            // applyTheme() — reliable even when customStylingService config is
            // stale (e.g. brand set from embed-dev-portal without a page reload).
            // Fall back to service config for the initial-load case where
            // applyTheme hasn't been called yet this session.
            const rawColor =
                getCurrentSourceColor() ||
                customStylingService.getConfig()?.getRawAppPanelColor?.() ||
                null;
            const resolvedMode = resolveThemeMode(rawColor, true);
            const hasBrand =
                resolvedMode.radiant && resolvedMode.mode === 'brand';
            let navMode: 'brand' | 'dark' | 'light';
            if (hasBrand) {
                navMode = 'brand';
            } else if (isDark) {
                navMode = 'dark';
            } else {
                navMode = 'light';
            }
            setRadiantNavMode(navMode);
        };

        const handleUserThemeChange = (e: Event) => {
            const { mode } = (e as CustomEvent).detail;

            // Remove any previous 'system' matchMedia listener before applying
            // the new mode, in case the user switches from system → light/dark.
            if (activeMediaQuery && activeMediaListener) {
                activeMediaQuery.removeEventListener(
                    'change',
                    activeMediaListener,
                );
                activeMediaQuery = null;
                activeMediaListener = null;
            }

            const isDark =
                mode === 'dark' ||
                (mode === 'system' &&
                    window.matchMedia('(prefers-color-scheme: dark)').matches);
            applyMode(isDark);

            // For 'system' mode, register an ongoing listener so OS-level
            // light/dark changes (e.g. sunset schedule) are reflected live.
            if (mode === 'system') {
                activeMediaQuery = window.matchMedia(
                    '(prefers-color-scheme: dark)',
                );
                activeMediaListener = (mq: MediaQueryListEvent) => {
                    applyMode(mq.matches);
                };
                activeMediaQuery.addEventListener(
                    'change',
                    activeMediaListener,
                );
            }
        };

        document.addEventListener(
            'rd:user-theme-changed',
            handleUserThemeChange,
        );

        // On mount, if the user already has 'system' preference saved, install
        // the matchMedia listener immediately. Without this, OS-level dark/light
        // changes are ignored until the user opens and re-saves their preference.
        const userId = sessionService?.getUserGuid?.() ?? '';
        const savedMode = getUserThemeModePref(userId);
        if (savedMode === 'system') {
            activeMediaQuery = window.matchMedia(
                '(prefers-color-scheme: dark)',
            );
            activeMediaListener = (mq: MediaQueryListEvent) => {
                applyMode(mq.matches);
            };
            activeMediaQuery.addEventListener('change', activeMediaListener);
        }

        return () => {
            document.removeEventListener(
                'rd:user-theme-changed',
                handleUserThemeChange,
            );
            if (activeMediaQuery && activeMediaListener) {
                activeMediaQuery.removeEventListener(
                    'change',
                    activeMediaListener,
                );
            }
        };
    }, [radiantEnabled]);

    // Set data-theme-type and handle legacy theme service.
    useEffect(() => {
        document
            .getElementById('root')
            ?.setAttribute('data-theme-type', effectiveThemeType);

        // Legacy flow: set top nav internal CSS vars from custom color.
        // Only runs when radiant nav bridge is NOT active.
        if (!radiantNavMode && themeType === blinkConstants.themeType.CUSTOM) {
            setTopNavInternalVariables(
                customStylingService.getConfig()?.getAppPanelColor().color,
            );
        }
    }, [
        themeType,
        leftNavThemeType,
        customStylingService,
        radiantEnabled,
        radiantNavMode,
    ]);

    if (
        !hideNavigationHub &&
        !isPageContentFullHeight &&
        (!isEmbedded || isFullAppEmbed())
    ) {
        return (
            <NavHubContextProvider>
                <Layout isAppLayout>
                    <LeftSideBar
                        className={cx(styles.appLayoutLeftSideBar, {
                            [styles.borderRight]: isLeftPanelVisible(
                                isOverlayPanel,
                                isGlobalNavHidden,
                                isInsightsPage,
                                sessionService,
                                appModule,
                            ),
                            [styles.noBorderRight]: !isLeftPanelVisible(
                                isOverlayPanel,
                                isGlobalNavHidden,
                                isInsightsPage,
                                sessionService,
                                appModule,
                            ),
                        })}
                        isLeftPanelExpanded={isLeftPanelVisible(
                            isOverlayPanel,
                            isGlobalNavHidden,
                            isInsightsPage,
                            sessionService,
                            appModule,
                        )}
                        isLeftPanelFloating={false}
                        leftPanelMinWidth={260}
                    >
                        <LeftNavigationHub
                            themeType={effectiveThemeType}
                            isPrimaryNavHidden={isGlobalNavHidden}
                        />
                    </LeftSideBar>
                    <Content
                        contentWidth={ContentWidth.FLUID}
                        noPadding
                        disableOverlayScroll
                        className={styles.content}
                    >
                        <Suspense
                            fallback={
                                <LoadingIndicator.Global
                                    className={styles.loadingIndicator}
                                >
                                    <></>
                                </LoadingIndicator.Global>
                            }
                        >
                            <ClientStateContextProvider>
                                <Suspense
                                    fallback={
                                        <LoadingIndicator.Global
                                            className={styles.loadingIndicator}
                                        >
                                            <></>
                                        </LoadingIndicator.Global>
                                    }
                                >
                                    <Outlet />
                                    {sessionService.isLoggedIn() && (
                                        <InitSessionCheckContainer />
                                    )}
                                </Suspense>
                            </ClientStateContextProvider>
                        </Suspense>
                    </Content>
                </Layout>
            </NavHubContextProvider>
        );
    }

    const hideSparkflowLoader =
        isSparkflowPinboardRoute && !isLoadingIndicatorVisible;

    return (
        <Suspense
            fallback={
                <LoadingIndicator.Global
                    className={styles.loadingIndicator}
                    hideLoader={hideSparkflowLoader}
                >
                    <></>
                </LoadingIndicator.Global>
            }
        >
            <ClientStateContextProvider>
                <Suspense
                    fallback={
                        <LoadingIndicator.Global
                            className={styles.loadingIndicator}
                            hideLoader={hideSparkflowLoader}
                        >
                            <></>
                        </LoadingIndicator.Global>
                    }
                >
                    <Outlet />
                    {sessionService.isLoggedIn() && (
                        <InitSessionCheckContainer />
                    )}
                </Suspense>
            </ClientStateContextProvider>
        </Suspense>
    );
};
